import React from "react";
import { Box, Chip, FormControl, FormLabel, Grid, Option, Stack, Switch } from "@mui/joy";
import type { ZekayCustomer } from "../../../../../api/customers/Customer.typed";
import type { QualificationItem } from "../../../../../api/qualification/Qualification.typed";
import type { ProfileFormState } from "../../hooks/profileFormTypes";
import type { Product } from "../../../../../api/billing/Billing.typed";
import { useBilling } from "../../../../../contexts/api/billing";
import { useTraining } from "../../../../../contexts/api";
import { DEFAULT_TAX_RATE, formatPrice, htToTtc } from "../../../../billing/shared/billingUtils";
import DarkTypography from "../../../../common/DarkTypography";
import DarkInput from "../../../../common/DarkInput";
import DarkDatePicker from "../../../../common/DarkDatePicker";
import DarkCheckbox from "../../../../common/DarkCheckbox";
import DarkSelect from "../../../../common/DarkSelect";
import PhotoComponent from "./PhotoComponent";
import ProfileFormSection from "./ProfileFormSection";
import ExcursionClientEmojiPicker from "../../../../excursions/shared/display/ExcursionClientEmojiPicker";
import {
  profileFormFieldLabelSx,
  profileFormIdentityPhotoColumnSx,
  textTokens,
} from "../../../../../styles/globalStyles";
import CustomerCountersTab from "../../../customerProducts/CustomerCountersTab";

type ProfileFormEditProps = {
  form: ProfileFormState;
  setForm: React.Dispatch<React.SetStateAction<ProfileFormState>>;
  customer: ZekayCustomer;
  canEditProfile: boolean;
  setCustomerUpdated: React.Dispatch<React.SetStateAction<ZekayCustomer | undefined>>;
  desync?: boolean;
  qualificationItems?: QualificationItem[];
  showCustomerProducts?: boolean;
  showAssociateProductInCreate?: boolean;
  associateProductIds: number[];
  setAssociateProductIds: React.Dispatch<React.SetStateAction<number[]>>;
};

export default function ProfileFormEdit({
  form,
  setForm,
  customer,
  canEditProfile,
  setCustomerUpdated,
  desync = false,
  qualificationItems = [],
  showCustomerProducts = false,
  showAssociateProductInCreate = false,
  associateProductIds,
  setAssociateProductIds,
}: ProfileFormEditProps) {
  const { listProducts } = useBilling();
  const { qualifications, refreshTrainingTemplates } = useTraining();
  const [productOptions, setProductOptions] = React.useState<Product[]>([]);

  const labelSx = { mb: 0.5 } as const;

  React.useEffect(() => {
    if (!showAssociateProductInCreate) return;
    void listProducts().then((data) => {
      if (data) setProductOptions(data.filter((p) => p.IsActive));
    });
  }, [showAssociateProductInCreate, listProducts]);

  React.useEffect(() => {
    if (showAssociateProductInCreate && !qualifications) void refreshTrainingTemplates();
  }, [showAssociateProductInCreate, qualifications, refreshTrainingTemplates]);

  const qualifMap = React.useMemo(() => {
    const m = new Map<number, string>();
    qualifications?.forEach((q) => m.set(q.ID, `${q.Agency} – ${q.Qualification}`));
    return m;
  }, [qualifications]);

  const toggleAssociateProduct = React.useCallback(
    (productId: number, checked: boolean) => {
      setAssociateProductIds((prev) => {
        if (checked && !prev.includes(productId)) {
          return [...prev, productId];
        }
        if (!checked) {
          return prev.filter((id) => id !== productId);
        }

        return prev;
      });
    },
    [setAssociateProductIds],
  );

  const associateTrainingIds = React.useMemo<number[]>(() => {
    const seen = new Set<number>();
    for (const pid of associateProductIds) {
      const p = productOptions.find((x) => x.ID === pid);
      if (!p) continue;
      for (const line of p.Lines ?? []) {
        const ids = line.prestation?.TrainingTemplateIDs ?? [];
        for (const id of ids) seen.add(id);
      }
    }

    return [...seen];
  }, [associateProductIds, productOptions]);

  const hasCustomerId = customer.Id != null;

  return (
    <Stack spacing={{ xs: 2, sm: 2.5 }} sx={{ maxWidth: "100%" }}>
      <ProfileFormSection title="Identité">
        <Grid container spacing={{ xs: 2, sm: 2.5 }} alignItems="flex-start">
          <Grid xs={12} md={8} sx={{ minWidth: 0 }}>
            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              <Grid xs={12} sm={6} sx={{ minWidth: 0 }}>
                <FormControl sx={{ minWidth: 0, width: "100%" }}>
                  <FormLabel sx={profileFormFieldLabelSx}>Prénom *</FormLabel>
                  <DarkInput
                    value={form.FirstName}
                    onChange={(e) => setForm((f) => ({ ...f, FirstName: e.target.value }))}
                    placeholder="Prénom"
                    fullWidth
                  />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6} sx={{ minWidth: 0 }}>
                <FormControl sx={{ minWidth: 0, width: "100%" }}>
                  <FormLabel sx={profileFormFieldLabelSx}>Nom *</FormLabel>
                  <DarkInput
                    value={form.LastName}
                    onChange={(e) => setForm((f) => ({ ...f, LastName: e.target.value }))}
                    placeholder="Nom"
                    fullWidth
                  />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6} sx={{ minWidth: 0 }}>
                <FormControl sx={{ minWidth: 0, width: "100%" }}>
                  <FormLabel sx={profileFormFieldLabelSx}>Date de naissance</FormLabel>
                  <DarkDatePicker
                    value={form.Birthdate}
                    onChange={(v) => setForm((f) => ({ ...f, Birthdate: v }))}
                    fullWidth
                  />
                </FormControl>
              </Grid>
              {canEditProfile && (
                <Grid xs={12} sx={{ minWidth: 0 }}>
                  <FormControl sx={{ minWidth: 0, width: "100%" }}>
                    <FormLabel sx={profileFormFieldLabelSx}>Mood</FormLabel>
                    <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
                      <ExcursionClientEmojiPicker
                        value={form.Emoji || undefined}
                        onChange={(em) => setForm((f) => ({ ...f, Emoji: em ?? "" }))}
                      />
                    </Box>
                  </FormControl>
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid xs={12} md={4} sx={profileFormIdentityPhotoColumnSx}>
            <Box
              sx={{
                width: "100%",
                maxWidth: "min(100%, 220px)",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <PhotoComponent
                customer={customer}
                setCustomerUpdated={setCustomerUpdated}
                readOnly={!canEditProfile || customer?.Id == null}
              />
            </Box>
          </Grid>
        </Grid>
      </ProfileFormSection>

      <ProfileFormSection title="Coordonnées">
        <Grid container spacing={{ xs: 1.5, sm: 2 }}>
          <Grid xs={12} sm={6}>
            <DarkTypography level="body-sm" colorVariant="secondary" sx={labelSx}>
              Email
            </DarkTypography>
            <DarkInput
              type="email"
              value={form.MailAddress}
              onChange={(e) => setForm((f) => ({ ...f, MailAddress: e.target.value }))}
              placeholder="email@exemple.fr"
              fullWidth
            />
          </Grid>
          <Grid xs={12} sm={6}>
            <DarkTypography level="body-sm" colorVariant="secondary" sx={labelSx}>
              Téléphone
            </DarkTypography>
            <DarkInput
              value={form.Phonenumber}
              onChange={(e) => setForm((f) => ({ ...f, Phonenumber: e.target.value }))}
              placeholder="Téléphone"
              fullWidth
            />
          </Grid>
          <Grid xs={12}>
            <DarkTypography level="body-sm" colorVariant="secondary" sx={labelSx}>
              Adresse
            </DarkTypography>
            <DarkInput
              value={form.Street}
              onChange={(e) => setForm((f) => ({ ...f, Street: e.target.value }))}
              placeholder="Rue, numéro"
              fullWidth
            />
          </Grid>
          <Grid xs={12} sm={4}>
            <DarkTypography level="body-sm" colorVariant="secondary" sx={labelSx}>
              Code postal
            </DarkTypography>
            <DarkInput
              value={form.PostalCode}
              onChange={(e) => setForm((f) => ({ ...f, PostalCode: e.target.value }))}
              placeholder="CP"
              fullWidth
            />
          </Grid>
          <Grid xs={12} sm={8}>
            <DarkTypography level="body-sm" colorVariant="secondary" sx={labelSx}>
              Ville
            </DarkTypography>
            <DarkInput
              value={form.City}
              onChange={(e) => setForm((f) => ({ ...f, City: e.target.value }))}
              placeholder="Ville"
              fullWidth
            />
          </Grid>
        </Grid>
      </ProfileFormSection>

      <ProfileFormSection title="Qualification">
        {desync && qualificationItems.length > 0 && canEditProfile ? (
          <>
            <DarkTypography level="body-sm" colorVariant="secondary" sx={labelSx}>
              Niveau
            </DarkTypography>
            <DarkSelect
              placeholder="Choisir une qualification"
              value={form.LastQualification || null}
              onChange={(_e, value) => setForm((f) => ({ ...f, LastQualification: value ?? "" }))}
            >
              {qualificationItems.map((q) => (
                <Option key={q.id} value={q.sigle}>
                  {q.name ? `${q.sigle} – ${q.name}` : q.sigle}
                </Option>
              ))}
            </DarkSelect>
          </>
        ) : (
          <DarkTypography level="body-sm" colorVariant="primary">
            Niveau : {customer?.LastQualification ?? "—"}
          </DarkTypography>
        )}
      </ProfileFormSection>

      <ProfileFormSection title="Newsletter">
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <DarkTypography level="body-sm" colorVariant="secondary">
            Recevoir la newsletter
          </DarkTypography>
          <Switch
            checked={form.ReceiveNewsletter}
            onChange={(e) => setForm((f) => ({ ...f, ReceiveNewsletter: e.target.checked }))}
            color={form.ReceiveNewsletter ? "success" : "neutral"}
          />
        </Box>
      </ProfileFormSection>

      {showAssociateProductInCreate && (
        <ProfileFormSection title="Produits">
          <Stack spacing={0.75} sx={{ minWidth: 0, width: "100%" }}>
            <Box
              sx={{
                maxHeight: { xs: "min(42dvh, 280px)", sm: "min(38dvh, 320px)" },
                overflowY: "auto",
                pr: 0.5,
                display: "flex",
                flexDirection: "column",
                gap: 0.75,
              }}
            >
              {productOptions.length === 0 ? (
                <DarkTypography level="body-sm" sx={{ color: textTokens.color.dimmed }}>
                  Aucun produit actif.
                </DarkTypography>
              ) : (
                productOptions.map((p) => {
                  const lineLabel = `${p.Name} — ${formatPrice(htToTtc(p.Price, p.TaxRate ?? DEFAULT_TAX_RATE), p.Currency || "EUR")} TTC${
                    p.IsUnlimited ? " (Illimité)" : ""
                  }`;

                  return (
                    <DarkCheckbox
                      key={p.ID}
                      checked={associateProductIds.includes(p.ID)}
                      onChange={(e) => toggleAssociateProduct(p.ID, e.target.checked)}
                      label={lineLabel}
                      sx={{
                        alignItems: "flex-start",
                        "& .MuiCheckbox-label": {
                          overflow: "hidden",
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                          wordBreak: "break-word",
                        },
                      }}
                    />
                  );
                })
              )}
            </Box>
          </Stack>
          {associateTrainingIds.length > 0 && (
            <Stack spacing={0.5} sx={{ mt: 1 }}>
              <DarkTypography level="body-sm" sx={{ color: textTokens.color.dimmed }}>
                Formations créées automatiquement à l&apos;achat :
              </DarkTypography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {associateTrainingIds.map((id) => (
                  <Chip key={id} size="sm" variant="soft" color="primary">
                    {qualifMap.get(id) ?? `Formation #${id}`}
                  </Chip>
                ))}
              </Stack>
            </Stack>
          )}
        </ProfileFormSection>
      )}

      {showCustomerProducts && customer.Id != null && (
        <ProfileFormSection title="Produits">
          <CustomerCountersTab customerId={customer.Id} />
        </ProfileFormSection>
      )}

      {hasCustomerId && (
        <ProfileFormSection title="Identifiants">
          <DarkTypography
            level="body-xs"
            colorVariant="muted"
            sx={{ fontStyle: "italic", display: "block" }}
          >
            ID : {customer?.Id != null ? customer.Id : "—"}
          </DarkTypography>
          <DarkTypography
            level="body-xs"
            colorVariant="muted"
            sx={{ fontStyle: "italic", display: "block", mt: 0.5 }}
          >
            ID externe : {customer?.ExternalId != null ? customer.ExternalId : "—"}
          </DarkTypography>
        </ProfileFormSection>
      )}
    </Stack>
  );
}
